import { memo, useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useGetBillsQuery } from "../../../store";
import { BillReceiptIcon } from "../../atoms/animatedIcons";
import { MascotLoadingOverlay } from "../../shared";
import BillDetailContent from "../../Firm/Tabs/BillDetailContent";
import { getBillTypeDetails } from "../../../utils/billTypeDetails";

const sameInstant = (a, b) => a && b && new Date(a).getTime() === new Date(b).getTime();

/**
 * Organizma: sipariş vermeyen satırındaki son siparişin fatura detayı (#367).
 * Uyarı çekmecesinde tablo sola daralır, bu panel sağda açılır.
 *
 * Kayıttaki `lastOrderInfo` (firmId + billNo + createdInfo) ile mevcut
 * `getMyBills` ucundan faturanın TÜM kalemleri çekilir; aynı fatura no'nun
 * aynı andaki satırları tek grup olarak gösterilir (Faturalar sekmesiyle
 * aynı gruplama). Gövde firma Faturalar çekmecesiyle ortak (BillDetailContent).
 *
 * @param {{ firm: object, product: object }} target açık satır
 */
function LastOrderBillPanel({ target, onClose, className = "" }) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const canRead = hasPermission("bills.read");

  const info = target?.product?.lastOrderInfo || null;
  const firmId = info?.firmId ? String(info.firmId) : null;
  const billNo = info?.billNo || "";

  const { data, isLoading, isFetching, isError, error } = useGetBillsQuery(
    { token: auth.accessToken, id: firmId, firmIds: [firmId], billNo, page: 1, pageSize: 20 },
    { skip: !canRead || !firmId || !billNo }
  );

  const group = useMemo(() => {
    const rows = (data?.data?.bills || []).filter((b) => b.billNo === billNo);
    if (rows.length === 0) return null;
    // Aynı fatura no ile birden fazla işlem olabilir: kayıttaki ana denk
    // gelen satırlar; yoksa en yeni işlemin satırları
    const exact = rows.filter((b) => sameInstant(b.createdInfo, info?.createdInfo));
    const latestAt = rows[0].createdInfo;
    const lines = exact.length ? exact : rows.filter((b) => sameInstant(b.createdInfo, latestAt));
    // Yalnız tıklanan ürünün kalemleri — tutar ve birim fiyat o ürünün (#367).
    // Ürün eşleşmezse (ad değişmiş vb.) faturanın tamamı gösterilir.
    const productName = target?.product?.productName;
    const own = lines.filter((l) => (l.productTypeName || l.productName) === productName);
    const shown = own.length ? own : lines;
    return { ...shown[0], lines: shown, otherCount: lines.length - shown.length };
  }, [data, billNo, info, target]);

  const orderedBy = target?.product?.orderedBy;
  const sourceNote =
    target?.product?.lastOrderSource === "parent" && orderedBy?.firmName
      ? `${orderedBy.firmName} faturası`
      : target?.product?.lastOrderSource === "transfer" && orderedBy?.firmName
        ? `${orderedBy.firmName} kota gönderdi`
        : null;

  let body;
  if (!canRead) {
    body = <p className="text-sm text-gray-500">Fatura detayını görmek için fatura okuma yetkiniz yok.</p>;
  } else if (!firmId || !billNo) {
    body = <p className="text-sm text-gray-500">Bu üründe kayıtlı bir son sipariş faturası yok.</p>;
  } else if (isLoading || (isFetching && !group)) {
    body = (
      <div className="relative min-h-[240px]">
        <MascotLoadingOverlay message="Fatura yükleniyor..." />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      </div>
    );
  } else if (isError) {
    body = <p className="text-sm text-red-600">{error?.data?.message || "Fatura detayı alınamadı."}</p>;
  } else if (!group) {
    body = <p className="text-sm text-gray-500">Fatura bulunamadı — silinmiş olabilir.</p>;
  } else {
    body = (
      <div className="space-y-3">
        <BillDetailContent bill={group} typeDetails={getBillTypeDetails(group.billType, group.quotaShare)} />
        {group.otherCount > 0 && (
          <p className="text-xs text-gray-400">
            Bu faturada başka ürünlerin {group.otherCount} kalemi daha var — yalnız {target?.product?.productName} gösteriliyor.
          </p>
        )}
      </div>
    );
  }

  return (
    <aside className={`flex min-h-0 flex-col bg-gray-50 ${className}`}>
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <BillReceiptIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-gray-800">
              Son Sipariş · {target?.product?.productName || "Ürün"}
            </h3>
            <p className="truncate text-xs text-gray-500">
              {target?.firm?.firmName}
              {billNo && <span className="font-mono"> · {billNo}</span>}
              {sourceNote && <span className="text-indigo-600"> · {sourceNote}</span>}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Kapat"
          className="shrink-0 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <FaTimes className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{body}</div>
    </aside>
  );
}

export default memo(LastOrderBillPanel);
